interface RuntimeEnv {
  VITE_API_BASE_URL?: string;
  VITE_API_URL?: string;
  VITE_WS_BASE_URL?: string;
}

export interface RuntimeUrlOptions {
  apiBaseUrl?: string | null;
  wsBaseUrl?: string | null;
}

const IMPLICIT_API_PREFIXES = ['/analyst', '/modules'] as const;

function currentEnv(): RuntimeEnv {
  return ((import.meta as ImportMeta).env ?? {}) as RuntimeEnv;
}

function normalizeBaseUrl(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().replace(/\/+$/, '');
  return trimmed.length > 0 ? trimmed : null;
}

function resolveApiBaseUrl(options: RuntimeUrlOptions = {}): string | null {
  if (Object.prototype.hasOwnProperty.call(options, 'apiBaseUrl')) {
    return normalizeBaseUrl(options.apiBaseUrl);
  }

  return normalizeBaseUrl(options.apiBaseUrl)
    ?? normalizeBaseUrl(currentEnv().VITE_API_BASE_URL)
    ?? normalizeBaseUrl(currentEnv().VITE_API_URL);
}

function resolveExplicitWsBaseUrl(options: RuntimeUrlOptions = {}): string | null {
  if (Object.prototype.hasOwnProperty.call(options, 'wsBaseUrl')) {
    return normalizeBaseUrl(options.wsBaseUrl);
  }

  return normalizeBaseUrl(options.wsBaseUrl)
    ?? normalizeBaseUrl(currentEnv().VITE_WS_BASE_URL);
}

function isAbsoluteNetworkUrl(url: string): boolean {
  return /^[a-z][a-z\d+\-.]*:\/\//i.test(url) || url.startsWith('//');
}

function normalizeBackendPath(path: string): string {
  if (path === '/api' || path.startsWith('/api/')) {
    return path;
  }

  if (IMPLICIT_API_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return `/api${path}`;
  }

  return path;
}

function toWebSocketProtocol(url: string): string {
  return url.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');
}

function deriveWsBaseFromApiBase(apiBaseUrl: string): string {
  const wsApiBase = toWebSocketProtocol(apiBaseUrl);
  return wsApiBase.endsWith('/api')
    ? `${wsApiBase.slice(0, -4)}/ws`
    : `${wsApiBase}/ws`;
}

function browserWebSocketOrigin(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
}

function rewriteSameOriginAbsoluteUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) {
      return url;
    }

    return buildBackendUrl(`${parsed.pathname}${parsed.search}${parsed.hash}`);
  } catch {
    return url;
  }
}

export function buildBackendUrl(
  path: string,
  options: RuntimeUrlOptions = {},
): string {
  if (!path.startsWith('/') || isAbsoluteNetworkUrl(path)) {
    return path;
  }

  const normalizedPath = normalizeBackendPath(path);
  const apiBaseUrl = resolveApiBaseUrl(options);

  if (!apiBaseUrl || !(normalizedPath === '/api' || normalizedPath.startsWith('/api/'))) {
    return normalizedPath;
  }

  return `${apiBaseUrl}${normalizedPath.slice('/api'.length)}`;
}

export function buildWebSocketUrl(
  path: string,
  options: RuntimeUrlOptions = {},
): string {
  if (!path.startsWith('/') || isAbsoluteNetworkUrl(path)) {
    return path;
  }

  const explicitWsBaseUrl = resolveExplicitWsBaseUrl(options);
  if (path === '/ws' || path.startsWith('/ws/')) {
    if (explicitWsBaseUrl) {
      return `${explicitWsBaseUrl}${path.slice('/ws'.length)}`;
    }

    const apiBaseUrl = resolveApiBaseUrl(options);
    if (apiBaseUrl) {
      return `${deriveWsBaseFromApiBase(apiBaseUrl)}${path.slice('/ws'.length)}`;
    }

    return `${browserWebSocketOrigin()}${path}`;
  }

  const normalizedPath = normalizeBackendPath(path);
  if (normalizedPath === '/api' || normalizedPath.startsWith('/api/')) {
    const apiBaseUrl = resolveApiBaseUrl(options);
    if (apiBaseUrl) {
      return `${toWebSocketProtocol(apiBaseUrl)}${normalizedPath.slice('/api'.length)}`;
    }

    return `${browserWebSocketOrigin()}${normalizedPath}`;
  }

  return path;
}

export function installBackendFetchRewrite(): void {
  if (typeof globalThis.fetch !== 'function') {
    return;
  }

  const existingFetch = globalThis.fetch as typeof fetch & { __mgsBackendRewriteInstalled?: boolean };
  if (existingFetch.__mgsBackendRewriteInstalled) {
    return;
  }

  const nativeFetch = globalThis.fetch.bind(globalThis);
  const wrappedFetch: typeof fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === 'string') {
      return nativeFetch(buildBackendUrl(input), init);
    }

    if (input instanceof URL) {
      const rewrittenUrl = rewriteSameOriginAbsoluteUrl(input.toString());
      return nativeFetch(rewrittenUrl === input.toString() ? input : rewrittenUrl, init);
    }

    if (input instanceof Request) {
      const rewrittenUrl = rewriteSameOriginAbsoluteUrl(input.url);
      return nativeFetch(
        rewrittenUrl === input.url ? input : new Request(rewrittenUrl, input),
        init,
      );
    }

    return nativeFetch(input, init);
  }) as typeof fetch;

  (wrappedFetch as typeof fetch & { __mgsBackendRewriteInstalled?: boolean }).__mgsBackendRewriteInstalled = true;
  globalThis.fetch = wrappedFetch;
}
